import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import FeedList from "@/components/FeedList";
import UserAvatar from "@/components/UserAvatar";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { toast } from "sonner";
import { Users2, UserPlus, Plus, Check, Loader2, Sparkles } from "lucide-react";

/**
 * Following
 *   - "People you follow" — list of accounts the current user follows
 *     (backend: GET /api:brontoy-users-prod/me/following) with a compact
 *     "recent posts" feed underneath so the tab still feels alive.
 *   - "Explore who to follow" — algorithmic follow recommendations
 *     (backend: GET /api:brontoy-users-prod/recommendations).
 */
export default function Following() {
  const [tab, setTab] = useState("people");
  return (
    <div className="max-w-2xl mx-auto w-full px-4 sm:px-6 py-6">
      <div className="mb-6">
        <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tighter">Following</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Your follow graph — and space to grow it.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full grid grid-cols-2 h-11 rounded-full p-1 mb-6 bg-foreground/[0.04]" data-testid="following-tabs">
          <TabsTrigger
            value="people"
            className="rounded-full h-9 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm inline-flex items-center gap-2"
            data-testid="following-tab-people"
          >
            <Users2 className="w-4 h-4" strokeWidth={1.6} />
            People you follow
          </TabsTrigger>
          <TabsTrigger
            value="explore"
            className="rounded-full h-9 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm inline-flex items-center gap-2"
            data-testid="following-tab-explore"
          >
            <UserPlus className="w-4 h-4" strokeWidth={1.6} />
            Explore who to follow
          </TabsTrigger>
        </TabsList>

        <TabsContent value="people" className="focus-visible:outline-none">
          <PeopleYouFollow />
        </TabsContent>
        <TabsContent value="explore" className="focus-visible:outline-none">
          <ExploreWhoToFollow />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// -------------------------------------------------------------------
// People you follow — hits GET /me/following with a lightweight
// "recent posts" strip below so returning users see fresh content
// even when they haven't scrolled the feed yet.
// -------------------------------------------------------------------
function PeopleYouFollow() {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(EP.myFollowing(1, 30));
        const d = unwrap(res.data);
        const list = d?.items || d?.users || (Array.isArray(d) ? d : []);
        setPeople(list);
      } catch (e) {
        setErr(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="space-y-3" data-testid="following-people-loading">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-16 rounded-2xl bg-foreground/5 animate-pulse" />
        ))}
      </div>
    );
  }
  if (err) {
    return <div className="rounded-3xl border border-destructive/40 bg-destructive/10 text-destructive p-6" data-testid="following-people-error">{err}</div>;
  }
  if (people.length === 0) {
    return (
      <div className="rounded-3xl border border-border p-10 text-center" data-testid="following-people-empty">
        <Users2 className="w-6 h-6 mx-auto mb-3 text-muted-foreground/70" strokeWidth={1.4} />
        <p className="text-sm text-muted-foreground mb-1">You&apos;re not following anyone yet.</p>
        <p className="text-xs text-muted-foreground mb-5">Discover people to make your feed come alive.</p>
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="following-tab-explore"]')?.click(); }}
          className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-foreground text-background text-sm font-medium hover:bg-foreground/90"
          data-testid="following-empty-explore-cta"
        >
          <Sparkles className="w-4 h-4" strokeWidth={1.6} /> Find people to follow
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2" data-testid="following-people-list">
        {people.map((u) => (
          <PersonRow key={u.id ?? u.user_id} user={u} initiallyFollowed />
        ))}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-3">
          Recent from your follows
        </div>
        <FeedList
          endpointFn={EP.feedFollowing}
          testid="feed-following-page"
          fallbackToOwnPosts={false}
          emptyState={
            <div className="rounded-3xl border border-border p-8 text-center text-muted-foreground text-sm" data-testid="feed-following-empty">
              Your follows haven&apos;t posted yet. Check back soon.
            </div>
          }
        />
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Explore who to follow — hits the recommendations endpoint with a
// legacy suggested_users fallback so we never render a blank tab.
// -------------------------------------------------------------------
function ExploreWhoToFollow() {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Preferred: algorithmic recommendations
        const res = await api.get(EP.followRecommendations(1, 30));
        const d = unwrap(res.data);
        let list = d?.items || d?.users || (Array.isArray(d) ? d : []);
        // Fallback to the onboarding-era suggested_users when the recos
        // pool hasn't warmed up yet.
        if (list.length === 0) {
          try {
            const res2 = await api.get(EP.suggestedUsers(1, 30));
            const d2 = unwrap(res2.data);
            list = d2?.items || d2?.users || [];
          } catch { /* silent */ }
        }
        setPeople(list);
      } catch (e) {
        setErr(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-16 rounded-2xl bg-foreground/5 animate-pulse" />
        ))}
      </div>
    );
  }
  if (err) {
    return <div className="rounded-3xl border border-destructive/40 bg-destructive/10 text-destructive p-6" data-testid="explore-follow-error">{err}</div>;
  }
  if (!people.length) {
    return (
      <div className="rounded-3xl border border-border p-10 text-center text-muted-foreground" data-testid="explore-follow-empty">
        Nothing to suggest yet. Follow a few interests to unlock suggestions.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="explore-follow-list">
      {people.map((u) => (
        <PersonRow key={u.id ?? u.user_id} user={u} />
      ))}
    </div>
  );
}

// -------------------------------------------------------------------
// Shared person row — used by both People-you-follow (initiallyFollowed
// = true, so button shows Following) and Explore (initiallyFollowed
// = false, or true when the API tells us `viewer_is_following`).
// -------------------------------------------------------------------
function PersonRow({ user, initiallyFollowed = false }) {
  const id = user.id ?? user.user_id;
  const [followed, setFollowed] = useState(
    initiallyFollowed || !!(user.viewer_is_following || user.is_following)
  );
  const [pending, setPending] = useState(false);

  const toggle = async () => {
    if (pending) return;
    setPending(true);
    const next = !followed;
    setFollowed(next);
    try {
      if (next) {
        // iOS spec: prefer POST /follow_by_username { user_id, username }.
        if (user?.username) {
          await api.post(EP.followByUsername, { user_id: id, username: user.username });
        } else {
          await api.post(EP.followUser(id), {});
        }
      } else {
        await api.delete(EP.unfollowUser(id));
      }
    } catch (e) {
      setFollowed(!next);
      toast.error(apiError(e));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-2xl border border-border hover:border-foreground/25 transition-colors" data-testid={`follow-row-${id}`}>
      <Link to={`/${user.username}`} className="flex items-center gap-3 flex-1 min-w-0">
        <UserAvatar user={user} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{user.display_name || user.username}</div>
          <div className="text-xs text-muted-foreground truncate">@{user.username}</div>
          {user.bio && (
            <div className="text-xs text-muted-foreground truncate mt-0.5">{user.bio}</div>
          )}
        </div>
      </Link>
      <button
        onClick={toggle}
        disabled={pending}
        className={`h-9 rounded-full px-4 text-xs font-medium border transition-all inline-flex items-center gap-1.5 ${
          followed ? "bg-foreground text-background border-foreground hover:opacity-90" : "border-border hover:bg-foreground/5"
        }`}
        data-testid={`follow-toggle-${id}`}
      >
        {pending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : followed ? (
          <>
            <Check className="w-3.5 h-3.5" /> Following
          </>
        ) : (
          <>
            <Plus className="w-3.5 h-3.5" /> Follow
          </>
        )}
      </button>
    </div>
  );
}
